import { useEffect, useMemo, useRef, useState } from "react";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@material-ui/core";
import { GetApp, Refresh, Search } from "@material-ui/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  getAbsiteProgramSummary,
  resetAbsiteProgramSummary
} from "../../actions";
import Menu from "../menu/menu";
import { trackProgramSummaryViewed } from "../../utils/tracking";
import {
  buildProgramMetricsMarkdownReport,
  downloadProgramMetricsReport,
  followUpSignal,
  getProgramMetricsReportFilename
} from "./program-metrics-export";
import "./program-metrics.scss";

const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;
const formatScore = (value) => Number(value || 0).toFixed(1);

export default function ProgramMetricsPage() {
  const dispatch = useDispatch();
  const [programCode, setProgramCode] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const summaryTrackedRef = useRef("");
  const isAdmin = window.localStorage.getItem("role") === "ROLE_ADMIN";
  const summaryState = useSelector((state) => state.absiteProgramSummary);
  const programs = useMemo(
    () => summaryState?.programSummary?.programs || [],
    [summaryState?.programSummary?.programs]
  );

  useEffect(() => {
    if (isAdmin) {
      dispatch(getAbsiteProgramSummary({}));
    }

    return () => dispatch(resetAbsiteProgramSummary());
  }, [dispatch, isAdmin]);

  const totals = useMemo(() => programs.reduce((acc, program) => ({
    programCount: acc.programCount + 1,
    startedCount: acc.startedCount + (program.startedCount || 0),
    completedCount: acc.completedCount + (program.completedCount || 0),
    uniqueResidentCount: acc.uniqueResidentCount + (program.uniqueResidentCount || 0),
  }), {
    programCount: 0,
    startedCount: 0,
    completedCount: 0,
    uniqueResidentCount: 0,
  }), [programs]);

  const completionRate = totals.startedCount
    ? (totals.completedCount * 100) / totals.startedCount
    : 0;

  useEffect(() => {
    if (!isAdmin || summaryState?.loading || !summaryState?.programSummary) {
      return;
    }

    const signature = [
      totals.programCount,
      totals.startedCount,
      totals.completedCount
    ].join("|");

    if (summaryTrackedRef.current === signature) {
      return;
    }

    summaryTrackedRef.current = signature;
    trackProgramSummaryViewed({
      programCount: totals.programCount,
      startedCount: totals.startedCount,
      completedCount: totals.completedCount
    });
  }, [
    isAdmin,
    summaryState?.loading,
    summaryState?.programSummary,
    totals.programCount,
    totals.startedCount,
    totals.completedCount
  ]);

  const onApplyFilters = () => {
    dispatch(getAbsiteProgramSummary({
      programCode,
      from,
      to
    }));
  };

  const onResetFilters = () => {
    setProgramCode("");
    setFrom("");
    setTo("");
    dispatch(getAbsiteProgramSummary({}));
  };

  const onExportReport = () => {
    const generatedAt = new Date();

    downloadProgramMetricsReport({
      filename: getProgramMetricsReportFilename(generatedAt),
      report: buildProgramMetricsMarkdownReport({
        generatedAt,
        filters: {
          programCode,
          from,
          to
        },
        totals: {
          ...totals,
          completionRate
        },
        programs
      })
    });
  };

  if (!isAdmin) {
    return (
      <Box component="div" className="body-cont-over program-metrics-page">
        <Menu />
        <Box component="main" className="program-metrics-shell">
          <Paper elevation={0} className="program-metrics-denied">
            <Typography component="h1">Program Metrics</Typography>
            <Typography component="p">Admin access required.</Typography>
          </Paper>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="div" className="body-cont-over program-metrics-page">
      <Menu />

      <Backdrop open={!!summaryState?.loading} className="result-loader">
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box component="main" className="program-metrics-shell">
        <Box component="header" className="program-metrics-header">
          <Box component="div">
            <Typography component="h1">Program Metrics</Typography>
            <Typography component="p">ABSITE diagnostic pilot evidence</Typography>
          </Box>
          <Button
            color="primary"
            variant="outlined"
            onClick={onExportReport}
            startIcon={<GetApp />}
            disabled={!!summaryState?.loading || !programs.length}
          >
            Export report
          </Button>
        </Box>

        <Paper elevation={0} className="program-metrics-filters">
          <TextField
            label="Program code"
            variant="outlined"
            value={programCode}
            onChange={(event) => setProgramCode(event.target.value)}
            size="small"
          />
          <TextField
            label="From"
            type="date"
            variant="outlined"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To"
            type="date"
            variant="outlined"
            value={to}
            onChange={(event) => setTo(event.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <Button
            color="primary"
            variant="contained"
            onClick={onApplyFilters}
            startIcon={<Search />}
          >
            Apply
          </Button>
          <Button
            color="primary"
            variant="outlined"
            onClick={onResetFilters}
            startIcon={<Refresh />}
          >
            Reset
          </Button>
        </Paper>

        {summaryState?.errorMsg && (
          <Box component="div" className="program-metrics-error">
            {summaryState.errorMsg}
          </Box>
        )}

        <Box component="section" className="program-metrics-cards">
          <Paper elevation={0} className="program-metrics-card">
            <span>Programs</span>
            <strong>{totals.programCount}</strong>
          </Paper>
          <Paper elevation={0} className="program-metrics-card">
            <span>Started</span>
            <strong>{totals.startedCount}</strong>
          </Paper>
          <Paper elevation={0} className="program-metrics-card">
            <span>Completed</span>
            <strong>{totals.completedCount}</strong>
          </Paper>
          <Paper elevation={0} className="program-metrics-card">
            <span>Completion</span>
            <strong>{formatPercent(completionRate)}</strong>
          </Paper>
        </Box>

        <TableContainer component={Paper} elevation={0} className="program-metrics-table-wrap">
          <Table className="program-metrics-table">
            <TableHead>
              <TableRow>
                <TableCell>Program</TableCell>
                <TableCell align="right">Started</TableCell>
                <TableCell align="right">Completed</TableCell>
                <TableCell align="right">Completion</TableCell>
                <TableCell align="right">Residents</TableCell>
                <TableCell align="right">Avg Score</TableCell>
                <TableCell>PGY</TableCell>
                <TableCell>Weak Areas</TableCell>
                <TableCell>Follow-up</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {programs.map((program) => (
                <TableRow key={program.programCode}>
                  <TableCell className="program-code-cell">{program.programCode}</TableCell>
                  <TableCell align="right">{program.startedCount}</TableCell>
                  <TableCell align="right">{program.completedCount}</TableCell>
                  <TableCell align="right">{formatPercent(program.completionRate)}</TableCell>
                  <TableCell align="right">{program.uniqueResidentCount}</TableCell>
                  <TableCell align="right">{formatScore(program.averageScore)}</TableCell>
                  <TableCell>
                    <Box component="div" className="program-metrics-tags">
                      {Object.entries(program.pgyBreakdown || {}).map(([pgy, count]) => (
                        <span key={pgy}>{pgy}: {count}</span>
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box component="div" className="program-metrics-weak-areas">
                      {(program.topWeakAreas || []).map((area) => (
                        <span key={`${program.programCode}-${area.name}`}>
                          {area.name} ({formatPercent(area.percentageCorrect)})
                        </span>
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <span className={`program-metrics-signal ${followUpSignal(program).toLowerCase()}`}>
                      {followUpSignal(program)}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
              {!summaryState?.loading && !programs.length && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Box component="div" className="program-metrics-empty">
                      No program-coded diagnostics found.
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}
